/**
 * TELEVİZOR DEKODERİNİN KADR HƏDDİ — 1920×1080 (2026-10-24, MYTasks 27 «Pərdə və Akinak TV
 * brauzerdə açılmır»).
 *
 * ⚠ NƏ ÖLÇÜLDÜ (prod `aud.error_logs`, 14 gün): LG NetCast TV brauzeri (Chrome/79) İMZALI
 *   filmlərdə `native-video code=3` (MEDIA_ERR_DECODE) verirdi — hozu-xan (ən böyük pillə
 *   3840×2160) və oglan-evi-1 (2058×1152); oglan-evi-1-də bir dəfə `hls-media` də var. Pərdə
 *   və Akinak-ın ən böyük pilləsi 2048×858-dir (Mux API): hündürlüyü 1080-dən aşağıdır, amma
 *   eni televizor dekoderlərinin çoxunun həddi olan 1920-ni keçir. Pərdə üçün jurnalda TV xətası
 *   yoxdur — onun səbəbi ehtimaldır, real televizorda yoxlanmalıdır.
 * ⚠ ƏVVƏL: hls.js və Shaka ABR-ı 10 Mbit başlanğıc təxmini ilə (`TV_STARTUP_BANDWIDTH`) ilk
 *   seqmentdən ən böyük pilləni seçə bilirdi, dekoder isə onu aça bilmirdi.
 * ⚠ NİYƏ HƏM SERVERDƏ, HƏM BURADA: server veb TV biletinin tokeninə Mux `max_resolution`
 *   tavanı yazır (`Api/.../TelevisionRenditionCap.cs`) — native `<video>` yolunda pilləni
 *   brauzer seçdiyi üçün yeganə müdafiə odur. Mux isə `max_resolution`-un pilləni nəyə görə
 *   saydığını sənədləşdirmir; hls.js və Shaka yollarında bu süzgəc 1920×1080-dən böyük pilləni
 *   Mux-un qaydasından ASILI OLMAYARAQ çıxarır.
 * ⚠ ŞAQULİ KADR Shaka-dakı kimi çevrilir (uzun tərəf ≤ 1920, qısa tərəf ≤ 1080).
 */

export const TV_MAX_VIDEO_WIDTH = 1920
export const TV_MAX_VIDEO_HEIGHT = 1080

/**
 * Shaka-nın üst səviyyə `restrictions` açarı (`player.configure`). Həddi aşan variant
 * `allowedByApplication = false` olur: ABR onu seçmir və `getVariantTracks()`-dən də çıxır,
 * yəni keyfiyyət menyusunda görünmür (shaka-player 4.16.42 mənbəsində oxundu:
 * `StreamUtils.applyRestrictions` · `isPlayable`).
 */
export const SHAKA_TV_RESTRICTIONS = { maxWidth: TV_MAX_VIDEO_WIDTH, maxHeight: TV_MAX_VIDEO_HEIGHT }

/** Ölçüsü məlum və televizor dekoderinin həddindən böyük kadr. Ölçü yoxdursa `false`. */
export function exceedsTvDecoder(width?: number | null, height?: number | null): boolean {
  if (!width || !height) return false
  const long = width >= height ? width : height
  const short = width >= height ? height : width
  return long > TV_MAX_VIDEO_WIDTH || short > TV_MAX_VIDEO_HEIGHT
}

/** hls.js pilləsinin işlətdiyimiz sahələri. */
export interface SizedLevel {
  width?: number
  height?: number
}

/** hls.js nüsxəsinin bu süzgəcə lazım olan üzvləri (kitabxana CDN-dən gəlir, tiplənmir). */
export interface CappableHls {
  levels?: SizedLevel[]
  removeLevel?(index: number): void
  autoLevelCapping?: number
}

/**
 * Həddi aşan pillələrin indeksləri — AZALAN sırada (silmə aşağıdakı indeksləri sürüşdürmür).
 * ⚠ Bütün pillələr həddi aşırsa BOŞ siyahı: pillə qalmasa hls.js oynada bilməz, onda
 *   televizor ən azı cəhd etsin (server tavanı Mux-da həmişə aşağı pillə saxlayır).
 */
export function oversizedLevelIndexes(levels: SizedLevel[] | null | undefined): number[] {
  if (!levels || levels.length === 0) return []
  const out: number[] = []
  for (let i = levels.length - 1; i >= 0; i--) {
    const level = levels[i]
    if (level && exceedsTvDecoder(level.width, level.height)) out.push(i)
  }
  return out.length === levels.length ? [] : out
}

/**
 * hls.js-də həddi aşan pillələri çıxarır — `MANIFEST_PARSED` dinləyicisində çağırılır.
 *
 * ⚠ NİYƏ MƏHZ ORADA: hls.js 1.5.20 (`LevelController.onManifestLoaded`) əvvəl
 *   `MANIFEST_PARSED`-i göndərir, sonra `startLoad()` çağırır — yəni dinləyici ilk seqment
 *   seçilməzdən ƏVVƏL işləyir və başlanğıc pilləsi (`firstAutoLevel`) qalan pillələrdən seçilir.
 * ⚠ `removeLevel` hls.js-in öz xəta bərpasında da işlətdiyi açıq API-dir; pillə keyfiyyət
 *   menyusundan da (`hls.levels`) itir. Köhnə nüsxədə yoxdursa ehtiyat `autoLevelCapping`-dir:
 *   pillələr hündürlüyə görə artan sıradadır, tavan ilk həddi aşan pillədən bir aşağıdır.
 *
 * @returns çıxarılan (və ya tavanla bağlanan) pillə sayı
 */
export function capHlsLevels(hls: CappableHls): number {
  const levels = hls.levels
  const oversized = oversizedLevelIndexes(levels)
  if (oversized.length === 0 || !levels) return 0
  if (typeof hls.removeLevel === 'function') {
    for (let i = 0; i < oversized.length; i++) hls.removeLevel(oversized[i] as number)
    return oversized.length
  }
  let cap = -1
  for (let i = 0; i < levels.length; i++) {
    const level = levels[i]
    if (level && exceedsTvDecoder(level.width, level.height)) break
    cap = i
  }
  if (cap < 0) return 0
  hls.autoLevelCapping = cap
  return levels.length - 1 - cap
}
